import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import * as React from 'react';
import * as DialogPrimitive from '@radix-ui/react-dialog';
import { cn } from '../lib/cn.js';
import { createOverlayPrimitive } from './overlay.js';
import { useRestoreFocusOnClose, withOpenerCapture } from './use-restore-focus-on-close.js';
// Records where focus was when a controlled `open` flips to true — see the hook.
export const Dialog = withOpenerCapture(DialogPrimitive.Root, 'Dialog');
export const DialogTrigger = DialogPrimitive.Trigger;
export const DialogPortal = DialogPrimitive.Portal;
export const DialogClose = DialogPrimitive.Close;
export const DialogOverlay = createOverlayPrimitive(DialogPrimitive.Overlay, 'modal', DialogPrimitive.Overlay.displayName);
const DialogScrollPolicyContext = React.createContext('content');
/** Размер — подвалу (`confirm`: кнопки во всю ширину на узком экране). */
const DialogSizeContext = React.createContext('md');
function scrollPolicyForSize(size) {
    return size === 'page' || size === 'confirm' ? 'body' : 'content';
}
/**
 * Диалог рендерится в Radix-портале в корне документа и центрируется через
 * `translate(-50%, -50%)`, то есть выходит за safe-area-паддинги оболочки
 * приложения. На десктопе все инсеты нулевые (no-op); на устройстве с вырезом
 * эти вычитания — единственное, что не даёт диалогу залезть под Dynamic Island
 * / индикатор home и под боковые вырезы в ландшафте.
 */
const DIALOG_WIDTH_BUDGET = 'calc(100vw - 2rem - env(safe-area-inset-left) - env(safe-area-inset-right))';
function dialogHeightBudget(viewportHeight) {
    return `calc(${viewportHeight} - env(safe-area-inset-top) - env(safe-area-inset-bottom))`;
}
/**
 * Ширина каждого токена. Значения — литеральные строки в CSS-единицах и
 * подставляются в inline-`style`, а не в классы Tailwind, поэтому сканеру
 * Tailwind тут искать нечего (в отличие от вариантов в `overlay.tsx`, где
 * литеральность класса обязательна).
 *
 *  - `sm` 24rem — короткие диалоги на одно решение (выбор статуса, подтверждение).
 *  - `md` 32rem — УМОЛЧАНИЕ, обязано совпадать с прежним `min(32rem, …)`,
 *    иначе поедет каждый диалог, который сегодня ничего не задаёт.
 *  - `lg` 48rem — форма или редактор среднего размера (прежний `max-w-3xl`).
 *  - `xl` 72rem — самые широкие: раньше это были `60rem` и `1200px` (75rem).
 *  - `page` — та же ширина, что `xl` (см. докблок `DialogSize`).
 */
const dialogSizeWidth = {
    sm: '24rem',
    md: '32rem',
    lg: '48rem',
    xl: '72rem',
    page: '72rem',
    confirm: '32rem',
};
/**
 * `confirm` на узком экране (< 640 px, `max-sm:`) — полноэкранный. Габариты
 * широкого экрана у него идут не inline-стилем (тот не знает про медиа-запрос
 * и перебил бы классы), а CSS-переменными, которые подхватывают `sm:`-классы.
 * Все строки — литеральные, чтобы их нашёл сканер Tailwind.
 */
const CONFIRM_CLASSES = [
    'sm:max-w-[var(--dialog-max-w)] sm:max-h-[var(--dialog-max-h)]',
    'max-sm:left-0 max-sm:right-0 max-sm:top-[env(safe-area-inset-top)] max-sm:translate-x-0 max-sm:translate-y-0',
    'max-sm:h-[calc(100dvh-env(safe-area-inset-top)-env(safe-area-inset-bottom))] max-sm:max-w-none',
    'max-sm:rounded-none max-sm:border-0 max-sm:p-4',
].join(' ');
function dialogSizeStyle(size) {
    const maxWidth = `min(${dialogSizeWidth[size]}, ${DIALOG_WIDTH_BUDGET})`;
    if (size === 'confirm') {
        return {
            '--dialog-max-w': maxWidth,
            '--dialog-max-h': dialogHeightBudget('85dvh'),
        };
    }
    return size === 'page'
        ? // `height`, а не `maxHeight`: у потолка высота всё ещё зависит от
            // содержимого, а именно эта зависимость и заставляет коробку дышать.
            { maxWidth, height: dialogHeightBudget('90dvh') }
        : { maxWidth, maxHeight: dialogHeightBudget('85dvh') };
}
export const DialogContent = React.forwardRef(({ className, style, size = 'md', maxHeight, maxWidth, onCloseAutoFocus, children, ...props }, ref) => {
    const policy = scrollPolicyForSize(size);
    // State-opened dialogs have no Radix Trigger to return focus to — see the hook.
    const restoreFocus = useRestoreFocusOnClose(onCloseAutoFocus);
    return (_jsxs(DialogPortal, { children: [_jsx(DialogOverlay, {}), _jsx(DialogPrimitive.Content, { ref: ref, onCloseAutoFocus: restoreFocus, className: cn('fixed left-1/2 top-1/2 z-50 flex w-full -translate-x-1/2 -translate-y-1/2 flex-col gap-4 rounded-[var(--radius-4)] border border-[var(--color-border-default)] bg-[var(--color-surface-raised)] p-6 shadow-lg', 
                // Обе ветки — литеральные строки, чтобы сканер Tailwind их нашёл.
                policy === 'body' ? 'overflow-hidden' : 'overflow-y-auto', size === 'confirm' ? CONFIRM_CLASSES : undefined, className), style: {
                    ...dialogSizeStyle(size),
                    ...(maxHeight !== undefined ? { maxHeight } : {}),
                    ...(maxWidth !== undefined ? { maxWidth } : {}),
                    ...style,
                }, ...props, children: _jsx(DialogSizeContext.Provider, { value: size, children: _jsx(DialogScrollPolicyContext.Provider, { value: policy, children: children }) }) })] }));
});
DialogContent.displayName = DialogPrimitive.Content.displayName;
/**
 * Шапка диалога: заголовок и описание. НЕ прокручивается (`shrink-0`) — при
 * `size="page"` остаётся на месте, пока тело уезжает под ней.
 */
export const DialogHeader = React.forwardRef(({ className, ...props }, ref) => (_jsx("div", { ref: ref, className: cn('flex shrink-0 flex-col gap-1.5', className), ...props })));
DialogHeader.displayName = 'DialogHeader';
/**
 * Тело диалога — единственная прокручиваемая часть при `size="page"`.
 *
 * `min-h-0` здесь не украшение: во флекс-колонке у элемента
 * `min-height: auto`, поэтому без него тело не сжимается, растит родителя
 * вместо того чтобы прокручиваться, и фиксированная высота `page` перестаёт
 * работать.
 *
 * При политике `'content'` (все размеры кроме `page`) тело становится
 * `display: contents` — то есть исчезает из раскладки, и его дети остаются
 * прямыми флекс-элементами `DialogContent`, как были до обёртывания. Это не
 * косметика: три bulk-диалога держат список на `min-h-0 flex-1 overflow-auto`
 * ПРЯМЫМ ребёнком контента, и обычная обёртка разорвала бы эту цепочку —
 * вместо списка начал бы прокручиваться весь диалог. Заодно сохраняется
 * распределение `gap-4` между детьми и не появляется вторая полоса прокрутки
 * внутри уже прокручиваемого `DialogContent`.
 *
 * Практическое следствие: обернуть содержимое в `DialogBody` безопасно при
 * любом размере — до `page` оно не делает ровно ничего. Обратная сторона
 * `display: contents` — у элемента нет собственного бокса, поэтому фон,
 * рамка и внутренние отступы через `className` вступят в силу только при
 * `size="page"`.
 */
export const DialogBody = React.forwardRef(({ className, ...props }, ref) => {
    const policy = React.useContext(DialogScrollPolicyContext);
    return (_jsx("div", { ref: ref, className: cn(
        // Обе ветки — литеральные строки, чтобы сканер Tailwind их нашёл.
        policy === 'body' ? 'flex min-h-0 flex-1 flex-col gap-4 overflow-y-auto' : 'contents', className), ...props }));
});
DialogBody.displayName = 'DialogBody';
/**
 * Подвал диалога: кнопки действия. НЕ прокручивается (`shrink-0`) — именно то,
 * что модули собирали вручную парой `overflow-y-auto` + `justify-end`.
 */
export const DialogFooter = React.forwardRef(({ className, ...props }, ref) => {
    const size = React.useContext(DialogSizeContext);
    return (_jsx("div", { ref: ref, className: cn('flex shrink-0 justify-end gap-2', 
        // `confirm` на узком экране: кнопки во всю ширину, основная (последняя) — сверху
        size === 'confirm' ? 'max-sm:flex-col-reverse max-sm:[&>*]:w-full' : undefined, className), ...props }));
});
DialogFooter.displayName = 'DialogFooter';
export const DialogTitle = React.forwardRef(({ className, ...props }, ref) => (_jsx(DialogPrimitive.Title, { ref: ref, className: cn('text-lg font-semibold text-[var(--color-text-primary)]', className), ...props })));
DialogTitle.displayName = DialogPrimitive.Title.displayName;
export const DialogDescription = React.forwardRef(({ className, ...props }, ref) => (_jsx(DialogPrimitive.Description, { ref: ref, className: cn('text-sm text-[var(--color-text-muted)]', className), ...props })));
DialogDescription.displayName = DialogPrimitive.Description.displayName;
//# sourceMappingURL=dialog.js.map